import { changePersonId, type Change, type OrgModel, type SquadDirectory } from '@tive/core';

/**
 * The slice of the store the highlight filters read. Declared structurally so
 * this stays a plain function the tests can call with a literal, rather than
 * something that needs the whole app state stood up.
 */
export interface FilterState {
  model: OrgModel | null;
  changes: Change[];
  movedIds: ReadonlySet<string>;
  squads: SquadDirectory;
  onlyChanged: boolean;
  departmentFilter: string[];
  /** Squad ids, not names — a squad keeps its id through a rename. */
  squadFilter: string[];
}

/** Whether any filter is on at all. */
export function isFiltering(state: FilterState): boolean {
  return state.onlyChanged || state.departmentFilter.length > 0 || state.squadFilter.length > 0;
}

/**
 * Whether a person passes the filters.
 *
 * Several at once are an AND — pick Software and the Artemis team and you get
 * the people who are in both.
 */
export function matchesFilters(state: FilterState, personId: string): boolean {
  if (state.onlyChanged) {
    const touched =
      state.movedIds.has(personId) ||
      state.changes.some((change) => changePersonId(change) === personId) ||
      (state.model?.departures.has(personId) ?? false);
    if (!touched) return false;
  }

  if (state.departmentFilter.length > 0) {
    const department = state.model?.byId.get(personId)?.department ?? 'Unassigned';
    if (!state.departmentFilter.includes(department)) return false;
  }

  if (state.squadFilter.length > 0 && !onAnySquad(state.squads, state.squadFilter, personId)) {
    return false;
  }

  return true;
}

/**
 * Who the chart should draw: everyone who matches, plus the managers above them.
 *
 * Filtering used to fade people out, which cost exactly as much canvas as
 * showing them. Hiding them is the point — but a tree with its middle removed
 * is not a tree, so the managers between a match and the top stay as the thread
 * that holds it together. They are counted separately, and the chart says so.
 */
export interface FilterResult {
  /** Ids to draw, or null when nothing is filtered. */
  keep: Set<string> | null;
  matched: number;
  /** Managers kept only to connect the matches to the top. */
  context: number;
  total: number;
}

export function visibleAfterFilters(state: FilterState): FilterResult {
  const model = state.model;
  const total = model?.people.length ?? 0;
  if (!model || !isFiltering(state)) return { keep: null, matched: total, context: 0, total };

  const keep = new Set<string>();
  let matched = 0;

  for (const person of model.people) {
    if (!matchesFilters(state, person.id)) continue;
    matched += 1;

    let current: string | null = person.id;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      keep.add(current);
      current = model.byId.get(current)?.managerId ?? null;
    }
  }

  return { keep, matched, context: keep.size - matched, total };
}

function onAnySquad(squads: SquadDirectory, ids: string[], personId: string): boolean {
  return squads.squads.some(
    (squad) => ids.includes(squad.id) && squad.members.some((member) => member.personId === personId),
  );
}
